import { graphql, DataValue } from '@apollo/client/react/hoc';
import { flowRight as compose } from 'lodash';
import * as React from 'react';
import Modal from 'react-modal';

import { withRouter } from 'app/context/Router';
import ConfirmDialog from 'components/ConfirmDialog';
import CurrentUser from 'auth/CurrentUser';
import DeliveryModal from './DeliveryModal';
import ToastActions from 'actions/ToastActions';
import DELIVERY_SUMMARIES_FROM_STREAM from 'chat/queries/DeliverySummariesFromStreamQuery.gql';
import REQUEST_DELIVERY_FAILURES_MUTATION from 'chat/queries/RequestDeliveryFailuresReportMutation.gql';
import REQUEST_DELIVERY_UNREACHABLES_MUTATION from 'chat/queries/RequestDeliveryUnreachablesReportMutation.gql';
import SETTINGS_ROUTES from 'UserSettings/_shared/SettingsRouteConstants';
import MESSAGE_TRANSLATION_SUMMARY_QUERY from './MESSAGE_TRANSLATION_SUMMARY_QUERY.gql';

import type { DeliverySummariesFromStream } from 'chat/queries/__generated__/DeliverySummariesFromStream';
import type {
  fullDeliverySummary,
  // @ts-expect-error ts-migrate(2305) FIXME: Module '"chat/fragments/__generated__/fullDelivery... Remove this comment to see the full error message
  DeliveryReportInput,
} from 'chat/fragments/__generated__/fullDeliverySummary';
import { MessageTranslationSummary_api_messageTranslationSummary_summary as TranslationSummary } from './__generated__/MessageTranslationSummary';

type StreamDeliverySummary = {
  type: 'stream';
  threadUuid: string;
  messageUuid: string;
  targetTitle: string;
  targetDescription: string;
};

type AutoMessageSendDeliverySummary = {
  type: 'autoMessageSend';
  readSummary: fullDeliverySummary | undefined | null;
  threadUuid: string;
  messageUuid: string;
};

type Props = {
  router: any;
  loading: boolean;
  source: StreamDeliverySummary | AutoMessageSendDeliverySummary;
  onRequestHide: Function;
  data?: DataValue<DeliverySummariesFromStream>;
  requestDeliveryFailuresReport: (input: DeliveryReportInput) => Promise<any>;
  requestDeliveryUnreachablesReport: (input: DeliveryReportInput) => Promise<any>;
  translationSummaryLoading: boolean;
  translationSummary: TranslationSummary | null;
};

type State = {
  showMissingEmailModal: boolean;
};

class DeliveryModalContainer extends React.Component<Props, State> {
  state = {
    showMissingEmailModal: false,
  };

  componentDidUpdate(prevProps: Props) {
    const prevStreamItem = prevProps.data?.me?.tempStreamItem;
    const streamItem = this.props.data?.me?.tempStreamItem;

    if (!prevStreamItem && streamItem && streamItem.__typename !== 'MessageItem') {
      console.error('delivery modal to fetch delivery for non stream message item');
    }
  }

  getStreamItem = () => this.props.data?.me?.tempStreamItem;

  userHasNoEmail = () => !CurrentUser.hasEmail();

  toggleMissingEmailModal = () =>
    this.setState({ showMissingEmailModal: !this.state.showMissingEmailModal });

  handleDeliveryFailuresRequest = () => {
    if (this.userHasNoEmail()) {
      this.toggleMissingEmailModal();
      return;
    }
    const { threadUuid, messageUuid } = this.props.source;

    this.props
      .requestDeliveryFailuresReport({
        streamUuid: threadUuid,
        messageUuid,
      })
      .then(
        (response) => {
          const { error } = response.data.requestDeliveryFailuresReport;
          if (error) {
            ToastActions.showErrorToast(error.message);
            return;
          }
          ToastActions.showSuccessToast(
            'You’ve been sent an email with a link to download your CSV'
          );
        },
        (error) => {
          ToastActions.showDefaultErrorToast();
          console.warn(error);
          console.error('DeliveryModalContainer - failed to request delivery failures');
        }
      );
  };

  handleDeliveryUnreachablesRequest = () => {
    if (this.userHasNoEmail()) {
      this.toggleMissingEmailModal();
      return;
    }
    const { threadUuid, messageUuid } = this.props.source;

    this.props
      .requestDeliveryUnreachablesReport({
        streamUuid: threadUuid,
        messageUuid,
      })
      .then(
        (response) => {
          const { error } = response.data.requestDeliveryUnreachablesReport;
          if (error) {
            ToastActions.showErrorToast(error.message);
            return;
          }
          ToastActions.showSuccessToast(
            'You’ll receive an email with your CSV when it’s ready to download'
          );
        },
        (error) => {
          ToastActions.showDefaultErrorToast();
          console.warn(error);
          console.error('DeliveryModalContainer - failed to request delivery unreachables');
        }
      );
  };

  renderMissingEmailModal = () => {
    return (
      <Modal isOpen={this.state.showMissingEmailModal}>
        <ConfirmDialog
          title="Add an email address to your account first"
          subtext="CSV downloads are sent to your email. You can add an email address in your account settings."
          actionButtonText="Go to Settings"
          cancelButtonText="Cancel"
          onConfirm={() => this.props.router.push(SETTINGS_ROUTES.NOTIFICATIONS)}
          onCancel={this.toggleMissingEmailModal}
          onRequestHide={this.toggleMissingEmailModal}
          confirmEventKey="delivery_summary.no_email_modal.go_to_settings.click"
          cancelEventKey="delivery_summary.no_email_modal.cancel.click"
          eventKey="delivery_summary.no_email_modal.view"
        />
      </Modal>
    );
  };

  render() {
    const { data, onRequestHide, source, translationSummary, translationSummaryLoading } =
      this.props;
    let sentAt, sender;
    if (data?.me?.tempStreamItem.__typename === 'MessageItem') {
      sentAt = data?.me?.tempStreamItem.sentAt || undefined;
      sender = data?.me?.tempStreamItem.sender;
    }

    if (source.type === 'autoMessageSend') {
      return (
        <React.Fragment>
          {this.renderMissingEmailModal()}
          <DeliveryModal
            sentAt={sentAt}
            sender={sender}
            loading={false}
            onRequestHide={onRequestHide}
            messageUuid={source.messageUuid}
            messageReadSummaries={source.readSummary}
            onRequestDeliveryFailures={this.handleDeliveryFailuresRequest}
            onRequestDeliveryUnreachables={this.handleDeliveryUnreachablesRequest}
            translationSummary={translationSummary}
            translationSummaryLoading={translationSummaryLoading}
          />
        </React.Fragment>
      );
    }

    const streamItem = this.getStreamItem();

    if (!streamItem) {
      return (
        <DeliveryModal
          sentAt={sentAt}
          sender={sender}
          loading={data?.loading ?? false}
          messageUuid={source.messageUuid}
          onRequestHide={onRequestHide}
          translationSummary={translationSummary}
          translationSummaryLoading={translationSummaryLoading}
        />
      );
    }

    if (streamItem.__typename !== 'MessageItem') {
      return null;
    }

    const { targetTitle, targetDescription } = source;

    return (
      <React.Fragment>
        {this.renderMissingEmailModal()}
        <DeliveryModal
          sentAt={sentAt}
          sender={sender}
          loading={data?.loading || false}
          targetTitle={targetTitle}
          targetDescription={targetDescription}
          onRequestHide={onRequestHide}
          messageReadSummaries={streamItem.readSummary}
          reactionSummary={streamItem.reactionSummary}
          messageUuid={source.messageUuid}
          onRefetch={() => data?.refetch()}
          onRequestDeliveryFailures={this.handleDeliveryFailuresRequest}
          onRequestDeliveryUnreachables={this.handleDeliveryUnreachablesRequest}
          translationSummary={translationSummary}
          translationSummaryLoading={translationSummaryLoading}
        />
      </React.Fragment>
    );
  }
}

const withDeliverySummaryFromStream = graphql(DELIVERY_SUMMARIES_FROM_STREAM, {
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'source' does not exist on type '{}'.
  skip: ({ source }) => source.type !== 'stream',
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'source' does not exist on type '{}'.
  options: ({ source: { messageUuid, threadUuid } }) => ({
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    variables: {
      itemUuid: messageUuid,
      threadUuid,
    },
  }),
});

const withDeliveryFailuresReportMutation = graphql(REQUEST_DELIVERY_FAILURES_MUTATION, {
  props({ mutate }) {
    return {
      requestDeliveryFailuresReport(input: DeliveryReportInput) {
        // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        return mutate({
          variables: { input },
        });
      },
    };
  },
});

const withDeliveryUnreachablesReportMutation = graphql(REQUEST_DELIVERY_UNREACHABLES_MUTATION, {
  props({ mutate }) {
    return {
      requestDeliveryUnreachablesReport(input: DeliveryReportInput) {
        // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        return mutate({
          variables: { input },
        });
      },
    };
  },
});

const withTranslationSummary = graphql(MESSAGE_TRANSLATION_SUMMARY_QUERY, {
  skip: () => !CurrentUser.getUser().hasFeature('delivery_summary.web.jun-23'),
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'source' does not exist on type '{}'.
  options: ({ source: { messageUuid } }) => ({
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    variables: { uuid: messageUuid },
  }),
  props: ({ data, ownProps }: any) => {
    return {
      ...ownProps,
      translationSummaryLoading: !!data?.loading,
      translationSummary: data?.api_messageTranslationSummary?.summary || null,
    };
  },
});

export { DeliveryModalContainer };
export default compose(
  withRouter,
  withDeliverySummaryFromStream,
  withDeliveryFailuresReportMutation,
  withDeliveryUnreachablesReportMutation,
  withTranslationSummary
)(DeliveryModalContainer);
